import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  DateRange,
  extractDateRange,
  extractDefaultStoreId,
  extractNumber,
  HxPaymentApprovalService,
  HxStoreService,
  HxUiLabelPipe,
  PaymentApprovalModel,
  PaymentApprovalStatus,
  StoreBasicModel,
  toRouterQueryParams
} from 'hx-services';
import { Subscription } from 'rxjs';
import { HcToastrService } from '@manager-app/service/toastr.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbPagination, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import { PaymentApprovalViewComponent } from '@manager-app/storage/payment-approval/view/payment-approval-view.component';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { PaymentApprovalEditComponent } from '../edit/payment-approval-edit.component';
import { HxCheckboxComponent, HxDateRangeSelectComponent, HxLoaderComponent, HxStoreSelectComponent } from 'hx-component';
import { FormsModule } from '@angular/forms';
import { SuppliersSearchComponent } from '../../../components/suppliers-search/suppliers-search.component';
import { TranslocoPipe } from '@jsverse/transloco';

interface SelectablePaymentApproval extends PaymentApprovalModel {
  selected?: boolean;
}

@Component({
    selector: 'app-payment-approval-list',
    templateUrl: './payment-approval-list.component.html',
    styleUrls: ['./payment-approval-list.component.css'],
    imports: [NgIf, NgClass, PaymentApprovalEditComponent, HxStoreSelectComponent, FormsModule, HxDateRangeSelectComponent, SuppliersSearchComponent, HxCheckboxComponent, NgFor, NgbTooltip, HxLoaderComponent, NgbPagination, DatePipe, TranslocoPipe, HxUiLabelPipe]
})
export class PaymentApprovalListComponent implements OnInit, OnDestroy {
  @ViewChild('cancelTemplateRef') public cancelTemplateRef: TemplateRef<any>;
  @ViewChild('paidTemplateRef') public paidTemplateRef: TemplateRef<any>;
  list: SelectablePaymentApproval[] = [];
  statusOptions = new Map<PaymentApprovalStatus, { badge: string }>([
    [PaymentApprovalStatus.CREATED, {badge: 'info'}],
    [PaymentApprovalStatus.SUCCEED, {badge: 'success'}],
    [PaymentApprovalStatus.CANCELLED, {badge: 'danger'}],
    [PaymentApprovalStatus.WAIT_PAYMENT, {badge: 'warning'}],
  ]);
  isLoading = {
    list: false,
    paid: false,
    availableStore: false,
  };
  isAll = false;
  pagination = {allItemCount: 0};
  params: {
    page?: number,
    limit?: number,
    supplierId?: number,
    query?: string,
    status?: PaymentApprovalStatus,
    includeDisabled?: boolean,
    storeId?: number,
    date?: DateRange,
    paid?: boolean,
  } = {page: 1, limit: 20};
  paymentApprovalId: number;
  createdStatus = PaymentApprovalStatus.CREATED;
  selectedPaymentApproval: PaymentApprovalModel;
  selectedIds: number[] = [];
  reason: string;
  availableStoreIds: number[] = [];
  private sub: Subscription;

  constructor(
    private paymentApprovalService: HxPaymentApprovalService,
    private toastr: HcToastrService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private modal: NgbModal,
    private errorHandler: ErrorHandlerService,
    private storeService: HxStoreService,
  ) {}

  ngOnInit() {
    this.loadAvailableStore();
    this.sub = this.aRoute.queryParamMap.subscribe(paramMap => {
      const asNumberFn = (name: string, def?: number) => paramMap.get(name) ? Number(paramMap.get(name)) : def;
      const asBooleanFn = (name: string) => paramMap.get(name) ? paramMap.get(name) === 'true' : undefined;
      this.params.page = extractNumber('page', paramMap) ?? 1;
      this.params.limit = extractNumber('limit', paramMap) ?? 20;
      this.params.supplierId = asNumberFn('supplierId');
      this.params.date = extractDateRange('date', paramMap);
      this.params.includeDisabled = asBooleanFn('includeDisabled');
      this.params.status = paramMap.get('status') ? paramMap.get('status') as PaymentApprovalStatus : undefined;
      this.params.query = paramMap.get('query');
      this.params.storeId = extractNumber('storeId', paramMap) ?? extractDefaultStoreId();
      if (this.params.storeId) {
        this.loadList();
      }
    });
  }

  ngOnDestroy() {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  paidModal() {
    this.selectedIds = [];
    this.selectedIds = this.list.filter(si => si.selected).map(si => si.id);
    if (this.selectedIds.length > 0) {
      this.modal.open(this.paidTemplateRef, { size: 'sm', centered: true });
    } else {
      this.toastr.error('paymentApproval.not.selected');
    }
  }

  paidSingleModal(pa: SelectablePaymentApproval) {
    this.selectedIds = [];
    this.selectedIds = [pa.id];
    this.modal.open(this.paidTemplateRef, { size: 'sm', centered: true });
  }

  async paidConfirm(modal) {
    try {
      await this.paymentApprovalService.paidPaymentApproval({ids: this.selectedIds});
      this.toastr.success('paymentApproval.multiple.paid.success');
      this.loadList();
    } catch (err: any) {
      this.errorHandler.handler(err?.error);
    } finally {
      modal.dismiss();
    }
  }

  checkAll(): void {
    this.list.filter(r => !r.paymentDate).forEach(si => si.selected = this.isAll);
  }

  togglePaymentApproval(sp: SelectablePaymentApproval): void {
    sp.selected = !sp.selected;
  }

  resetFilter() {
    this.pagination.allItemCount = 0;
    this.params = {page: 1, limit: 20};
    this.navigateUrl();
  }

  pageChanged(page: number) {
    this.params.page = page;
    this.navigateUrl();
  }

  applyFilters() {
    this.pageChanged(1);
  }

  updateItem(pa: PaymentApprovalModel) {
    this.paymentApprovalId = pa.id;
  }

  cancelModal(pa: PaymentApprovalModel) {
    this.selectedPaymentApproval = {...pa};
    this.modal.open(this.cancelTemplateRef, { size: 'sm', centered: true });
  }

  async cancel(modal) {
    try {
      await this.paymentApprovalService.cancelPaymentApproval(this.selectedPaymentApproval.id, this.reason);
      this.toastr.success('paymentApproval.cancel.success');
      this.loadList();
    } catch (err: any) {
      this.errorHandler.handler(err?.error);
    } finally {
      modal.dismiss();
    }
  }

  onPaymentApprovalSaved(saved) {
    this.paymentApprovalId = null;
    if (saved) {
      this.params = {page: 1};
      this.loadList();
    }
  }

  view(pa: PaymentApprovalModel) {
    const modalRef = this.modal.open(PaymentApprovalViewComponent, {size: 'lg'});
    modalRef.componentInstance.id = pa.id;
    modalRef.result.then(() => {
      modalRef.dismiss();
    }, () => {
      modalRef.dismiss();
    });
  }

  onStoreFilterFn: (stores: StoreBasicModel[]) => StoreBasicModel[] = stores => {
    return stores.filter(r => this.availableStoreIds.includes(r.id));
  };

  private async loadList() {
    this.list = [];
    this.isLoading.list = true;
    try {
      const result = await this.paymentApprovalService.getPaymentApprovalList(this.params);
      this.list = result.list || [];
      this.pagination.allItemCount = result.count;
    } finally {
      this.isLoading.list = false;
    }
  }

  private navigateUrl() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  private loadAvailableStore() {
    this.availableStoreIds = [];
    this.isLoading.availableStore = true;
    this.storeService.getAvailableForPaymentApprovals().subscribe(result => {
      this.availableStoreIds = result.map(r => r.id);
      this.isLoading.availableStore = false;
    }, err => {
      this.isLoading.availableStore = false;
      this.errorHandler.handler(err.error);
    });
  }
}
